import React, { useState, useEffect, useMemo } from 'react';
import { DailyRecord, WeeklyRoutineTask, DashboardDailyMetrics, ScheduledOccurrence } from '../types';
import { 
  Calendar, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  Bell, 
  BellOff,
  X,
  Download,
  Copy,
  RotateCcw,
  ShieldCheck,
  Zap,
  CalendarCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Flame,
  GraduationCap,
  Activity,
  Briefcase,
  Utensils,
  CheckCheck,
  ArrowUpRight,
  ListPlus,
  Search,
  Filter,
  ExternalLink
} from 'lucide-react';
import { loadAppState, saveAppState, subscribeToAppSettings, syncSettingsFromCloud } from '../lib/storage';
import { getLocalDateString } from '../lib/dateUtils';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { generateWeeklyRoutineIcsContent, downloadIcsFile } from '../lib/calendar';
import VoiceInputButton from './VoiceInputButton';
import { 
  notifyPlannerChanged, 
  getDashboardMetricsForDate, 
  toggleOccurrenceStatus, 
  subscribeToPlannerChanges,
  getPlannerTasks,
  savePlannerTask,
  deletePlannerTask,
  mapPlannerTaskToWeeklyRoutineTask,
  mapWeeklyRoutineTaskToPlannerTask
} from '../lib/plannerClient';
import { getTaskDurationMinutes, formatMinutesDuration } from '../lib/scoringEngine';

interface WeeklyRoutineProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
  user?: any;
}

export default function WeeklyRoutineModule({ record, onUpdateRecord, user }: WeeklyRoutineProps) {
  const { lang, t } = useLanguage();
  const [activeSubTab, setActiveSubTab] = useState<'today' | 'manage'>('today');
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDay());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const WEEKDAYS = [
    { value: 6, labelFa: 'شنبه', labelEn: 'Saturday', labelSv: 'Lördag', shortFa: 'شنبه', shortEn: 'Sat', shortSv: 'Lör' },
    { value: 0, labelFa: 'یکشنبه', labelEn: 'Sunday', labelSv: 'Söndag', shortFa: 'یکشنبه', shortEn: 'Sun', shortSv: 'Sön' },
    { value: 1, labelFa: 'دوشنبه', labelEn: 'Monday', labelSv: 'Måndag', shortFa: 'دوشنبه', shortEn: 'Mon', shortSv: 'Mån' },
    { value: 2, labelFa: 'سه‌شنبه', labelEn: 'Tuesday', labelSv: 'Tisdag', shortFa: 'سه‌شنبه', shortEn: 'Tue', shortSv: 'Tis' },
    { value: 3, labelFa: 'چهارشنبه', labelEn: 'Wednesday', labelSv: 'Onsdag', shortFa: 'چهارشنبه', shortEn: 'Wed', shortSv: 'Ons' },
    { value: 4, labelFa: 'پنجشنبه', labelEn: 'Thursday', labelSv: 'Torsdag', shortFa: 'پنجشنبه', shortEn: 'Thu', shortSv: 'Tor' },
    { value: 5, labelFa: 'جمعه', labelEn: 'Friday', labelSv: 'Fredag', shortFa: 'جمعه', shortEn: 'Fri', shortSv: 'Fre' }
  ];

  const getDayName = (dayNum: number) => {
    const match = WEEKDAYS.find(w => w.value === dayNum);
    if (!match) return t("schedUnknownDay", "Unknown");
    if (lang === 'fa') return match.labelFa;
    if (lang === 'sv') return match.labelSv;
    return match.labelEn;
  };

  const getDayShortLabel = (w: typeof WEEKDAYS[0]) => {
    if (lang === 'fa') return w.shortFa;
    if (lang === 'sv') return w.shortSv;
    return w.shortEn;
  };

  const getLocalizedTaskContent = (task: WeeklyRoutineTask) => {
    let title = task.title;
    let desc = task.description;

    if (lang !== 'fa') {
      if (task.title.includes('فیزیوتراپی') || task.title.includes('ریکاوری')) {
        title = lang === 'sv' ? 'Morgonfysioterapi & återhämtning' : 'Morning Physiotherapy & Recovery';
      } else if (task.title.includes('شیفت کاری')) {
        title = lang === 'sv' ? 'Dagligt arbetspass' : 'Daily Work Shift';
      } else if (task.title.includes('کاردیو') || task.title.includes('چربی‌سوزی')) {
        title = lang === 'sv' ? 'Konditionsträning & fettförbränning' : 'Cardio & Fat Burning Window';
      }

      if (task.description?.includes('التیام کمر')) {
        desc = lang === 'sv' ? 'Skonsamma stretchövningar för rygglindring.' : 'Gentle stretches for back pain relief.';
      } else if (task.description?.includes('تمرکز روی کار')) {
        desc = lang === 'sv' ? 'Fokus på arbete. Res dig kl 12:00 för skonsam ryggstretch.' : 'Focus on work. Stand up at 12:00 and repeat gentle back stretches.';
      } else if (task.description?.includes('پیاده‌روی')) {
        desc = lang === 'sv' ? 'Promenad, stegträning eller mjuka bålrörelser.' : 'Walking, marching in place, or gentle abdominal twists.';
      }
    }

    return { title, description: desc };
  };

  const getCategoryConfig = (category?: string) => {
    const cat = (category || 'work').toLowerCase();
    if (cat.includes('rehab') || cat.includes('physio')) {
      return {
        key: 'rehab',
        label: t("schedulesCatRehab", "Physiotherapy & Back Rehab"),
        icon: <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />,
        colorBg: 'bg-emerald-50 dark:bg-emerald-950/50',
        colorText: 'text-emerald-800 dark:text-emerald-300',
        colorBorder: 'border-emerald-200 dark:border-emerald-800/80',
        accentBar: 'border-r-4 border-r-emerald-500'
      };
    }
    if (cat.includes('fat-loss') || cat.includes('workout') || cat.includes('exercise')) {
      return {
        key: 'fat-loss',
        label: t("schedulesCatFatLoss", "Workout & Cardio"),
        icon: <Flame className="w-4 h-4 text-rose-600 dark:text-rose-400" />,
        colorBg: 'bg-rose-50 dark:bg-rose-950/50',
        colorText: 'text-rose-800 dark:text-rose-300',
        colorBorder: 'border-rose-200 dark:border-rose-800/80',
        accentBar: 'border-r-4 border-r-rose-500'
      };
    }
    if (cat.includes('swedish') || cat.includes('sfi') || cat.includes('study') || cat.includes('edu')) {
      return {
        key: 'swedish',
        label: t("schedulesCatSwedish", "Education & Learning (Swedish/SFI)"),
        icon: <GraduationCap className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
        colorBg: 'bg-amber-50 dark:bg-amber-950/50',
        colorText: 'text-amber-800 dark:text-amber-300',
        colorBorder: 'border-amber-200 dark:border-amber-800/80',
        accentBar: 'border-r-4 border-r-amber-500'
      };
    }
    if (cat.includes('nutrition') || cat.includes('food')) {
      return {
        key: 'nutrition',
        label: t("schedulesCatNutrition", "Nutrition & Health"),
        icon: <Utensils className="w-4 h-4 text-teal-600 dark:text-teal-400" />,
        colorBg: 'bg-teal-50 dark:bg-teal-950/50',
        colorText: 'text-teal-800 dark:text-teal-300',
        colorBorder: 'border-teal-200 dark:border-teal-800/80',
        accentBar: 'border-r-4 border-r-teal-500'
      };
    }
    if (cat.includes('routine')) {
      return {
        key: 'routine',
        label: t("schedulesCatRoutine", "Personal Routine"),
        icon: <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />,
        colorBg: 'bg-purple-50 dark:bg-purple-950/50',
        colorText: 'text-purple-800 dark:text-purple-300',
        colorBorder: 'border-purple-200 dark:border-purple-800/80',
        accentBar: 'border-r-4 border-r-purple-500'
      };
    }
    // Default work
    return {
      key: 'work',
      label: t("schedulesCatWork", "Work & Employment"),
      icon: <Briefcase className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />,
      colorBg: 'bg-indigo-50 dark:bg-indigo-950/50',
      colorText: 'text-indigo-800 dark:text-indigo-300',
      colorBorder: 'border-indigo-200 dark:border-indigo-800/80',
      accentBar: 'border-r-4 border-r-indigo-500'
    };
  };

  const createGoogleCalendarUrl = (task: WeeklyRoutineTask) => {
    const loc = getLocalizedTaskContent(task);
    const title = encodeURIComponent(loc.title);
    const details = encodeURIComponent(loc.description || t("schedulesHeaderTitle", "Weekly Planner & Daily Routine"));
    
    const now = new Date();
    const currentDay = now.getDay();
    const targetDay = Number(task.dayOfWeek);
    let dayDiff = targetDay - currentDay;
    if (dayDiff < 0) dayDiff += 7;
    
    const eventDate = new Date(now);
    eventDate.setDate(now.getDate() + dayDiff);
    
    const [sh, sm] = (task.time || '08:00').split(':').map(Number);
    eventDate.setHours(sh, sm, 0, 0);
    
    const endDate = new Date(eventDate);
    if (task.endTime) {
      const [eh, em] = task.endTime.split(':').map(Number);
      endDate.setHours(eh, em, 0, 0);
    } else {
      endDate.setHours(sh + 1, sm, 0, 0);
    }
    
    const formatTimeISO = (d: Date) => d.toISOString().replace(/-|:|\.\d+/g, '');
    const dates = `${formatTimeISO(eventDate)}/${formatTimeISO(endDate)}`;
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${dates}&recur=RRULE:FREQ=WEEKLY`;
  };

  // AppState & routine data
  const [appState, setAppState] = useState(loadAppState());
  const [weeklyRoutine, setWeeklyRoutine] = useState<WeeklyRoutineTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(true);

  // Authoritative D1 occurrences for today
  const [todayMetrics, setTodayMetrics] = useState<DashboardDailyMetrics | null>(null);

  const loadTasksFromD1 = async () => {
    try {
      setIsLoadingTasks(true);
      const tasksFromD1 = await getPlannerTasks();
      const mapped = tasksFromD1.map(mapPlannerTaskToWeeklyRoutineTask);
      mapped.sort((a, b) => a.time.localeCompare(b.time));
      setWeeklyRoutine(mapped);
    } catch (e) {
      console.warn('Failed to load planner tasks from D1', e);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  useEffect(() => {
    syncSettingsFromCloud().catch(() => {});
    const unsubscribe = subscribeToAppSettings((newSettings) => {
      setAppState(prev => ({
        ...prev,
        settings: newSettings
      }));
    });

    loadTasksFromD1();

    const todayStr = getLocalDateString();
    let isMounted = true;
    const fetchOccs = async () => {
      try {
        const m = await getDashboardMetricsForDate(todayStr);
        if (isMounted && m) setTodayMetrics(m);
      } catch (e) {
        console.warn('Failed to load planner metrics', e);
      }
    };
    fetchOccs();

    const unsubPlanner = subscribeToPlannerChanges(() => {
      loadTasksFromD1();
      fetchOccs();
    });

    return () => {
      unsubscribe();
      unsubPlanner();
      isMounted = false;
    };
  }, []);

  // Task form modal states
  const [isEditingTask, setIsEditingTask] = useState<boolean>(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [taskForm, setTaskForm] = useState<Partial<WeeklyRoutineTask>>({
    dayOfWeek: 1,
    time: '08:00',
    endTime: '',
    title: '',
    description: '',
    category: 'work',
    reminderEnabled: false,
    phoneNotificationEnabled: true,
    wentToWorkChecked: false,
    attendedSfiChecked: false,
    isRequired: true
  });

  const handleToggleTaskCompletion = async (taskId: string, wentToWorkChecked?: boolean, attendedSfiChecked?: boolean) => {
    const currentCompleted = record.completedRoutineIds || [];
    const isCurrentlyDone = currentCompleted.includes(taskId);
    let updatedCompleted: string[];
    let shouldWentToWork = record.wentToWork;
    let shouldAttendedSfi = record.attendedSfi;

    if (isCurrentlyDone) {
      updatedCompleted = currentCompleted.filter(id => id !== taskId);
      if (wentToWorkChecked) shouldWentToWork = false;
      if (attendedSfiChecked) shouldAttendedSfi = false;
    } else {
      updatedCompleted = [...currentCompleted, taskId];
      if (wentToWorkChecked) shouldWentToWork = true;
      if (attendedSfiChecked) shouldAttendedSfi = true;
    }

    onUpdateRecord({
      ...record,
      completedRoutineIds: updatedCompleted,
      wentToWork: shouldWentToWork,
      attendedSfi: shouldAttendedSfi
    });

    const todayStr = getLocalDateString();
    const occ = todayMetrics?.occurrences.find(o => o.plannerTaskId === taskId || o.id === taskId);
    if (occ) {
      await toggleOccurrenceStatus(todayStr, occ.id, !isCurrentlyDone ? 'completed' : 'pending');
    } else {
      await toggleOccurrenceStatus(todayStr, taskId, !isCurrentlyDone ? 'completed' : 'pending');
    }
  };

  const handleCopySelectedDayToAllWeek = async () => {
    const dayName = getDayName(selectedDay);
    if (!window.confirm(t("schedulesConfirmCopyWeek", "Do you want to copy the schedule for {{day}} to all days of the week? This will overwrite existing routines.", { day: dayName }))) return;
    
    const dayTasks = weeklyRoutine.filter(t => Number(t.dayOfWeek) === selectedDay);
    if (dayTasks.length === 0) {
      alert(t("schedulesNoRoutinesForDay", "No routines scheduled for {{day}}.", { day: dayName }));
      return;
    }

    for (let day = 0; day <= 6; day++) {
      if (day === selectedDay) continue;
      
      // Clear previous tasks for this target day
      const existingForDay = weeklyRoutine.filter(t => Number(t.dayOfWeek) === day);
      for (const oldTask of existingForDay) {
        await deletePlannerTask(oldTask.id);
      }

      // Add copied tasks for this target day into D1
      for (const tCopy of dayTasks) {
        const payload = mapWeeklyRoutineTaskToPlannerTask({
          ...tCopy,
          id: undefined,
          dayOfWeek: day
        });
        await savePlannerTask(payload);
      }
    }
    notifyPlannerChanged();
  };

  const handlePrevManageDay = () => {
    const order = [1, 2, 3, 4, 5, 6, 0];
    const currIdx = order.indexOf(selectedDay);
    const prevIdx = (currIdx - 1 + order.length) % order.length;
    setSelectedDay(order[prevIdx]);
  };

  const handleNextManageDay = () => {
    const order = [1, 2, 3, 4, 5, 6, 0];
    const currIdx = order.indexOf(selectedDay);
    const nextIdx = (currIdx + 1) % order.length;
    setSelectedDay(order[nextIdx]);
  };

  const handleSelectTodayManage = () => {
    setSelectedDay(new Date().getDay());
  };

  const handleToggleTaskReminder = async (id: string) => {
    const target = weeklyRoutine.find(t => t.id === id);
    if (!target) return;
    const updated = { ...target, reminderEnabled: !target.reminderEnabled };
    await savePlannerTask(mapWeeklyRoutineTaskToPlannerTask(updated));
  };

  const handleToggleTaskNotification = async (id: string) => {
    const target = weeklyRoutine.find(t => t.id === id);
    if (!target) return;
    const updated = { ...target, phoneNotificationEnabled: !(target.phoneNotificationEnabled ?? true) };
    await savePlannerTask(mapWeeklyRoutineTaskToPlannerTask(updated));
  };

  const handleDuplicateTaskToDay = async (task: WeeklyRoutineTask, targetDay: number) => {
    const loc = getLocalizedTaskContent(task);
    const payload = mapWeeklyRoutineTaskToPlannerTask({
      ...task,
      id: undefined,
      dayOfWeek: targetDay
    });
    const res = await savePlannerTask(payload);
    if (res.success) {
      alert(t("schedulesCopiedSuccess", "Activity '{{title}}' copied to {{day}} successfully.", {
        title: loc.title,
        day: getDayName(targetDay)
      }));
    }
  };

  const handleDownloadWeeklyIcs = () => {
    const enabledTasks = weeklyRoutine.filter(t => t.reminderEnabled);
    if (enabledTasks.length === 0) {
      alert(t("schedulesNoCalendarAlert", "No activities have been marked with "));
      return;
    }
    const content = generateWeeklyRoutineIcsContent(weeklyRoutine);
    if (!content) return;
    downloadIcsFile('progress-weekly-schedule.ics', content);
  };

  // Routine task CRUD helpers
  const handleOpenAddTodayTask = () => {
    const todayNum = new Date().getDay();
    setSelectedDay(todayNum);
    setTaskForm({
      dayOfWeek: todayNum,
      time: '08:00',
      endTime: '',
      title: '',
      description: '',
      category: 'work',
      reminderEnabled: true,
      phoneNotificationEnabled: true,
      wentToWorkChecked: false,
      attendedSfiChecked: false,
      isRequired: true
    });
    setEditingTaskId(null);
    setIsEditingTask(true);
    setActiveSubTab('manage');
  };

  const handleOpenAddTask = () => {
    setTaskForm({
      dayOfWeek: selectedDay,
      time: '08:00',
      endTime: '',
      title: '',
      description: '',
      category: 'work',
      reminderEnabled: true,
      phoneNotificationEnabled: true,
      wentToWorkChecked: false,
      attendedSfiChecked: false,
      isRequired: true
    });
    setEditingTaskId(null);
    setIsEditingTask(true);
    setActiveSubTab('manage');
  };

  const handleOpenEditTask = (task: WeeklyRoutineTask) => {
    setTaskForm({
      ...task,
      phoneNotificationEnabled: task.phoneNotificationEnabled ?? true,
      isRequired: task.isRequired !== false
    });
    setEditingTaskId(task.id);
    setIsEditingTask(true);
  };

  const handlePresetSelect = (presetType: 'work' | 'rehab' | 'education' | 'cardio' | 'nutrition') => {
    if (presetType === 'work') {
      setTaskForm(prev => ({
        ...prev,
        title: t("schedulesPresetWorkTitle", "Daily Work Shift"),
        time: '08:00',
        endTime: '16:00',
        category: 'work',
        wentToWorkChecked: true,
        attendedSfiChecked: false,
        isRequired: true,
        description: t("schedulesPresetWorkDesc", "Work focus with 5-minute break intervals for back stretches.")
      }));
    } else if (presetType === 'rehab') {
      setTaskForm(prev => ({
        ...prev,
        title: t("schedulesPresetRehabTitle", "Physiotherapy & Back Rehab Exercises"),
        time: '07:30',
        endTime: '08:00',
        category: 'rehab',
        wentToWorkChecked: false,
        attendedSfiChecked: false,
        isRequired: true,
        description: t("schedulesPresetRehabDesc", "Back disc stretches, morning rehab, and core stabilization.")
      }));
    } else if (presetType === 'education') {
      setTaskForm(prev => ({
        ...prev,
        title: t("schedulesPresetEduTitle", "Educational Session (Swedish / Course)"),
        time: '17:00',
        endTime: '19:00',
        category: 'swedish',
        wentToWorkChecked: false,
        attendedSfiChecked: true,
        isRequired: true,
        description: t("schedulesPresetEduDesc", "Swedish class, specialized courses, or skill learning.")
      }));
    } else if (presetType === 'cardio') {
      setTaskForm(prev => ({
        ...prev,
        title: t("schedulesPresetCardioTitle", "Cardio Workout & Fat Burning Walk"),
        time: '19:30',
        endTime: '20:15',
        category: 'fat-loss',
        wentToWorkChecked: false,
        attendedSfiChecked: false,
        isRequired: false,
        description: t("schedulesPresetCardioDesc", "Brisk walking, low-impact cardio, and fat burning.")
      }));
    } else if (presetType === 'nutrition') {
      setTaskForm(prev => ({
        ...prev,
        title: t("schedulesPresetNutritionTitle", "Main Meal & Nutrition Planning"),
        time: '13:00',
        endTime: '13:30',
        category: 'nutrition',
        wentToWorkChecked: false,
        attendedSfiChecked: false,
        isRequired: true,
        description: t("schedulesPresetNutritionDesc", "Adequate protein intake, supplements, and hydration.")
      }));
    }
  };

  const handleSaveTask = async () => {
    if (!taskForm.title?.trim()) {
      return;
    }

    const finalIsRequired = taskForm.isRequired !== false;
    const taskToSave: Partial<WeeklyRoutineTask> = {
      ...(taskForm as WeeklyRoutineTask),
      id: editingTaskId || undefined,
      dayOfWeek: taskForm.dayOfWeek ?? selectedDay,
      time: taskForm.time || '12:00',
      endTime: taskForm.endTime || undefined,
      title: taskForm.title.trim(),
      description: taskForm.description,
      category: taskForm.category || 'work',
      reminderEnabled: taskForm.reminderEnabled || false,
      phoneNotificationEnabled: taskForm.phoneNotificationEnabled ?? true,
      isRequired: finalIsRequired
    };

    const payload = mapWeeklyRoutineTaskToPlannerTask(taskToSave);
    const res = await savePlannerTask(payload);

    if (res.success) {
      setIsEditingTask(false);
      setEditingTaskId(null);
    } else {
      alert(res.error || t("schedulesErrorSave", "Error saving activity to D1 database"));
    }
  };

  const handleDeleteTask = async (id: string) => {
    if (!window.confirm(t("schedulesConfirmDelete", "Are you sure you want to delete this routine?"))) return;
    const success = await deletePlannerTask(id);
    if (!success) {
      alert(t("schedulesErrorDelete", "Error deleting activity from D1 database"));
    }
  };

  // Day calculations
  const todayDayOfWeek = new Date().getDay();
  const todayTasks = weeklyRoutine.filter(t => Number(t.dayOfWeek) === todayDayOfWeek);
  
  // Filtered Tasks for Manage Tab
  const selectedDayTasks = useMemo(() => {
    let list = weeklyRoutine.filter(t => Number(t.dayOfWeek) === selectedDay);
    
    if (categoryFilter !== 'all') {
      list = list.filter(t => {
        const cfg = getCategoryConfig(t.category);
        return cfg.key === categoryFilter || (t.category || 'work') === categoryFilter;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(t => 
        t.title.toLowerCase().includes(q) || 
        (t.description && t.description.toLowerCase().includes(q))
      );
    }

    return list;
  }, [weeklyRoutine, selectedDay, categoryFilter, searchQuery]);

  // Overall Weekly Statistics
  const weeklyStats = useMemo(() => {
    let totalTasks = weeklyRoutine.length;
    let totalMinutes = 0;
    let workCount = 0;
    let eduCount = 0;

    weeklyRoutine.forEach(t => {
      totalMinutes += getTaskDurationMinutes(t.time, t.endTime);
      if (t.wentToWorkChecked || (t.category || '').includes('work')) workCount++;
      if (t.attendedSfiChecked || (t.category || '').includes('swedish') || (t.category || '').includes('sfi')) eduCount++;
    });

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const durationFormatted = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

    const completedTodayCount = (record.completedRoutineIds || []).length;
    const todayTotal = todayTasks.length;
    const todayPct = todayTotal > 0 ? Math.round((completedTodayCount / todayTotal) * 100) : 0;

    return {
      totalTasks,
      durationFormatted,
      workCount,
      eduCount,
      todayPct,
      completedTodayCount,
      todayTotal
    };
  }, [weeklyRoutine, todayTasks, record.completedRoutineIds]);

  // Time-based active task detection
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const isTaskActiveNow = (task: WeeklyRoutineTask) => {
    if (Number(task.dayOfWeek) !== todayDayOfWeek) return false;
    const [sh, sm] = (task.time || '00:00').split(':').map(Number);
    const startM = sh * 60 + sm;
    let endM = startM + 30;
    if (task.endTime) {
      const [eh, em] = task.endTime.split(':').map(Number);
      endM = eh * 60 + em;
    }
    return currentMinutes >= startM && currentMinutes < endM;
  };

  // Calculate weekly attendance overview
  const allRecords = appState.records || {};
  const currentWeekDate = new Date();
  const currentDayNum = currentWeekDate.getDay();

  const weekStart = new Date(currentWeekDate);
  const diffToSaturday = (currentDayNum + 1) % 7; 
  weekStart.setDate(currentWeekDate.getDate() - diffToSaturday);

  const weeklyLogs: Array<{ date: string; label: string; wentToWork: boolean; attendedSfi: boolean }> = [];
  let totalWorkDays = 0;
  let totalSfiDays = 0;

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const dateStr = getLocalDateString(d);
    const dayRec = allRecords[dateStr];
    const went = dayRec?.wentToWork || false;
    const sfi = dayRec?.attendedSfi || false;
    if (went) totalWorkDays++;
    if (sfi) totalSfiDays++;

    const dayOfWeek = d.getDay();
    const match = WEEKDAYS.find(w => w.value === dayOfWeek);
    weeklyLogs.push({
      date: dateStr,
      label: match ? getDayName(match.value) : dateStr,
      wentToWork: went,
      attendedSfi: sfi
    });
  }

  // Calculated form duration preview
  const formDurationMinutes = useMemo(() => {
    if (!taskForm.time) return 0;
    return getTaskDurationMinutes(taskForm.time, taskForm.endTime);
  }, [taskForm.time, taskForm.endTime]);

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      
      {/* 1. Modern Header Banner */}
      <div className="rounded-2xl p-6 md:p-7 bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-900 text-white shadow-lg border border-indigo-800/60 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-extrabold border border-indigo-400/30">
              <CalendarCheck className="w-4 h-4 text-emerald-400" />
              <span>{t("schedulesHeaderSubtitle", "Weekly Planner & Routine Management")}</span>
            </div>
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight">
              {t("schedulesHeaderTitle", "Weekly Planner & Daily Routine")}
            </h2>
            <p className="text-xs md:text-sm text-indigo-200/90 max-w-2xl leading-relaxed font-medium">
              {t("schedulesHeaderDesc", "Plan and manage your personal, work, study, and daily routines for each day of the week.")}
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap self-start md:self-auto">
            <button
              onClick={handleDownloadWeeklyIcs}
              className="px-3.5 py-2.5 bg-indigo-800/80 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 shadow-sm border border-indigo-600/50 cursor-pointer"
              title={t("schedulesDownloadIcsTooltip", "Download comprehensive .ics calendar file based on weekly routine for native phone alarms")}
            >
              <Download className="w-4 h-4 text-emerald-300" />
              <span>{t("schedulesDownloadIcsBtn", "Download Weekly Routine (.ics)")}</span>
            </button>

            <button
              onClick={handleOpenAddTask}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 shadow-md hover:shadow-emerald-900/30 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>{t("schedulesAddRoutineBtn", "Add New Activity")}</span>
            </button>
          </div>
        </div>

        {/* Sub-Tab Navigation Strip */}
        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-indigo-800/80 flex-wrap">
          <button
            onClick={() => setActiveSubTab('today')}
            className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center gap-2 ${
              activeSubTab === 'today'
                ? 'bg-white text-indigo-950 shadow-md ring-2 ring-indigo-400'
                : 'text-indigo-200 hover:text-white hover:bg-indigo-800/60'
            }`}
          >
            <Clock className="w-4 h-4 text-indigo-600" />
            <span>{t("schedulesTabToday", "📋 Today")}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-extrabold ${
              activeSubTab === 'today' ? 'bg-indigo-100 text-indigo-900' : 'bg-indigo-700/60 text-indigo-200'
            }`}>
              {formatNumberByLang(todayTasks.length, lang)}
            </span>
          </button>

          <button
            onClick={() => setActiveSubTab('manage')}
            className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center gap-2 ${
              activeSubTab === 'manage'
                ? 'bg-white text-indigo-950 shadow-md ring-2 ring-indigo-400'
                : 'text-indigo-200 hover:text-white hover:bg-indigo-800/60'
            }`}
          >
            <Calendar className="w-4 h-4 text-indigo-600" />
            <span>{t("schedulesTabManage", "⚙️ Manage Weekly Schedule")}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-extrabold ${
              activeSubTab === 'manage' ? 'bg-indigo-100 text-indigo-900' : 'bg-indigo-700/60 text-indigo-200'
            }`}>
              {formatNumberByLang(weeklyRoutine.length, lang)}
            </span>
          </button>
        </div>
      </div>

      {/* 2. Weekly Statistics KPI Overview Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-2xs space-y-1">
          <span className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">{t("schedulesTotalWeeklyTasks", "Total Weekly Activities")}</span>
          <div className="flex items-center justify-between">
            <span className="text-lg font-black text-gray-900 dark:text-white font-mono">{formatNumberByLang(weeklyStats.totalTasks, lang)}</span>
            <ListPlus className="w-4 h-4 text-indigo-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-2xs space-y-1">
          <span className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">{t("schedulesTotalScheduledHours", "Scheduled Hours")}</span>
          <div className="flex items-center justify-between">
            <span className="text-lg font-black text-gray-900 dark:text-white font-mono">{weeklyStats.durationFormatted}</span>
            <Clock className="w-4 h-4 text-emerald-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-2xs space-y-1">
          <span className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">{t("schedulesTotalWorkShifts", "Total Work Shifts")}</span>
          <div className="flex items-center justify-between">
            <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 font-mono">{formatNumberByLang(weeklyStats.workCount, lang)}</span>
            <Briefcase className="w-4 h-4 text-indigo-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-2xs space-y-1">
          <span className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">{t("schedulesTotalClasses", "Total Study Classes")}</span>
          <div className="flex items-center justify-between">
            <span className="text-lg font-black text-amber-600 dark:text-amber-400 font-mono">{formatNumberByLang(weeklyStats.eduCount, lang)}</span>
            <GraduationCap className="w-4 h-4 text-amber-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-2xs space-y-1 col-span-2 sm:col-span-4 lg:col-span-1">
          <span className="text-[10px] font-extrabold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">{t("schedulesTodayCompletion", "Today")}</span>
          <div className="flex items-center justify-between">
            <span className="text-lg font-black text-emerald-600 dark:text-emerald-400 font-mono">{formatNumberByLang(weeklyStats.todayPct, lang)}{lang === 'fa' ? '٪' : '%'}</span>
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 font-mono">
              ({formatNumberByLang(weeklyStats.completedTodayCount, lang)} / {formatNumberByLang(weeklyStats.todayTotal, lang)})
            </span>
          </div>
        </div>
      </div>

      {/* 3. Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left/Center Column: Task Management */}
        <div className="lg:col-span-8 space-y-6">

          {/* SUB-TAB 1: TODAY'S TIMELINE */}
          {activeSubTab === 'today' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-200 dark:border-gray-700 shadow-xs space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4 border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
                      <span>{t("schedulesTodayScheduleTitle", "Today's Schedule & Routine ({{day}})", { day: getDayName(todayDayOfWeek) })}</span>
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t("schedulesTodayScheduleSub", "Mark your required tasks as done to update your daily status.")}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleOpenAddTodayTask}
                  className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-extrabold transition-colors flex items-center gap-1.5 self-start sm:self-auto cursor-pointer border border-indigo-200 dark:border-indigo-800"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t("schedulesAddQuickRoutine", "Add to Today")}</span>
                </button>
              </div>

              {/* Tasks List */}
              {todayTasks.length === 0 ? (
                <div className="p-8 text-center bg-gray-50 dark:bg-gray-900/40 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl space-y-3">
                  <AlertCircle className="w-8 h-8 text-indigo-400 mx-auto" />
                  <h4 className="text-sm font-extrabold text-gray-800 dark:text-gray-200">
                    {t("schedulesNoRoutinesToday", "No routines scheduled for today!")}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                    {t("schedulesNoRoutinesTodayDesc", "You can define work, educational, or athletic routines for today using the weekly management tab.")}
                  </p>
                  <button
                    onClick={() => setActiveSubTab('manage')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-extrabold hover:bg-indigo-700 transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>{t("schedulesGoToManage", "Go to Weekly Management")}</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayTasks.map((task) => {
                    const isCompleted = record.completedRoutineIds?.includes(task.id) || false;
                    const isCurrent = isTaskActiveNow(task);
                    const dur = getTaskDurationMinutes(task.time, task.endTime);
                    const loc = getLocalizedTaskContent(task);
                    const catCfg = getCategoryConfig(task.category);

                    return (
                      <div 
                        key={task.id}
                        className={`p-4 rounded-2xl border transition-all ${catCfg.accentBar} ${
                          isCompleted
                            ? 'bg-emerald-50/30 border-emerald-300 dark:bg-emerald-950/20 dark:border-emerald-900/60 opacity-80'
                            : isCurrent
                            ? 'bg-indigo-50/60 border-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-700 shadow-sm ring-1 ring-indigo-400/40'
                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700/80 hover:border-indigo-300 dark:hover:border-indigo-700 shadow-2xs'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="space-y-2 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Category Badge */}
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-extrabold border ${catCfg.colorBg} ${catCfg.colorText} ${catCfg.colorBorder}`}>
                                {catCfg.icon}
                                <span>{catCfg.label}</span>
                              </span>

                              {/* Time Range */}
                              <span className="px-2.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg font-mono text-xs font-bold border border-gray-200 dark:border-gray-700" dir="ltr">
                                🕒 {task.time}{task.endTime ? ` - ${task.endTime}` : ''}
                              </span>

                              {/* Duration */}
                              {dur > 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono font-bold">
                                  ({formatMinutesDuration(dur, lang)})
                                </span>
                              )}

                              {/* Requirement Status */}
                              {task.isRequired !== false ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 font-bold border border-indigo-200 dark:border-indigo-800">
                                  {t("dashTaskRequired", "Required")}
                                </span>
                              ) : (
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 font-bold border border-amber-200 dark:border-amber-800">
                                  {t("dashTaskOptional", "Optional")}
                                </span>
                              )}

                              {/* Active Pulse Badge */}
                              {isCurrent && (
                                <span className="text-[10px] px-2.5 py-0.5 bg-indigo-600 text-white rounded-md font-extrabold animate-pulse flex items-center gap-1">
                                  <span>📌 {t("schedulesCurrentActiveTask", "Active Now")}</span>
                                </span>
                              )}

                              {task.wentToWorkChecked && (
                                <span className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 rounded-md font-bold">
                                  {t("schedulesWorkTag", "Work Shift")}
                                </span>
                              )}

                              {task.attendedSfiChecked && (
                                <span className="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 rounded-md font-bold">
                                  {t("schedulesStudyTag", "Study / SFI")}
                                </span>
                              )}
                            </div>

                            <h4 className={`text-base font-black ${isCompleted ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                              {loc.title}
                            </h4>

                            {loc.description && (
                              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                                {loc.description}
                              </p>
                            )}
                          </div>

                          {/* Quick Toggle Actions */}
                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                            <button
                              onClick={() => handleToggleTaskCompletion(task.id, task.wentToWorkChecked, task.attendedSfiChecked)}
                              className={`px-4 py-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center gap-1.5 shadow-xs ${
                                isCompleted
                                  ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                              }`}
                            >
                              <Check className="w-4 h-4" />
                              <span>{isCompleted ? t("schedulesCompletedStatus", "Completed") : t("schedulesMarkComplete", "Mark Complete")}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* SUB-TAB 2: MANAGE ALL WEEK */}
          {activeSubTab === 'manage' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
              
              {/* Day Selector Tabs Strip (Saturday to Friday) */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    {t("schedulesSelectDayToManage", "Select day of the week to edit and plan:")}
                  </span>
                  <button
                    onClick={handleCopySelectedDayToAllWeek}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-extrabold cursor-pointer"
                    title={t("schedulesCopyDayToAllWeekTooltip", "Copy all tasks of selected day to all days of the week")}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>{t("schedulesCopyDayToAllWeek", "Copy this day to all week")}</span>
                  </button>
                </div>

                {/* 7-Day Matrix Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                  {WEEKDAYS.map((w) => {
                    const isSelected = selectedDay === w.value;
                    const isRealToday = new Date().getDay() === w.value;
                    const tasksForW = weeklyRoutine.filter(t => Number(t.dayOfWeek) === w.value);
                    const count = tasksForW.length;
                    
                    let dayMins = 0;
                    tasksForW.forEach(t => { dayMins += getTaskDurationMinutes(t.time, t.endTime); });
                    const dayHours = Math.floor(dayMins / 60);
                    const dayMinsRem = dayMins % 60;
                    const durStr = dayHours > 0 ? `${dayHours}h${dayMinsRem > 0 ? ` ${dayMinsRem}m` : ''}` : `${dayMinsRem}m`;

                    return (
                      <button
                        key={w.value}
                        type="button"
                        onClick={() => setSelectedDay(w.value)}
                        className={`p-3 rounded-2xl text-xs font-extrabold transition-all text-center flex flex-col items-center justify-center gap-1 cursor-pointer border ${
                          isSelected
                            ? 'bg-indigo-600 text-white border-indigo-700 shadow-md ring-2 ring-indigo-300 dark:ring-indigo-700 scale-[1.02]'
                            : 'bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-800/80 shadow-2xs'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-black">{getDayName(w.value)}</span>
                          {isRealToday && (
                            <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-amber-300' : 'bg-emerald-500'}`} title={t("schedulesTodayBadge", "Today")} />
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1 text-[10px] opacity-80">
                          <span>{getDayShortLabel(w)}</span>
                        </div>

                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                            isSelected 
                              ? 'bg-white/25 text-white' 
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                          }`}>
                            {formatNumberByLang(count, lang)} {t("schedTaskCountUnit", "tasks")}
                          </span>
                        </div>

                        {dayMins > 0 && (
                          <span className={`text-[9px] font-mono opacity-90 ${isSelected ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            ⏱️ {durStr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Filtering & Search Bar */}
              <div className="p-3.5 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-6 relative">
                  <Search className="w-4 h-4 text-gray-400 absolute right-3 top-3" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("schedulesSearchPlaceholder", "Search routine name or description...")}
                    className="w-full pr-9 pl-3 py-2 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-medium"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute left-3 top-2.5 text-xs text-gray-400 hover:text-gray-600">
                      ✕
                    </button>
                  )}
                </div>

                <div className="sm:col-span-6 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400 shrink-0" />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-bold"
                  >
                    <option value="all">{t("allCategories", "All Categories")}</option>
                    <option value="work">💼 {t("schedulesCatWork", "Work & Employment")}</option>
                    <option value="rehab">🛡️ {t("schedulesCatRehab", "Physiotherapy & Back Rehab")}</option>
                    <option value="fat-loss">🔥 {t("schedulesCatFatLoss", "Workout & Cardio")}</option>
                    <option value="swedish">🎓 {t("schedulesCatSwedish", "Education & Learning (Swedish/SFI)")}</option>
                    <option value="nutrition">🍎 {t("schedulesCatNutrition", "Nutrition & Health")}</option>
                    <option value="routine">✨ {t("schedulesCatRoutine", "Personal Routine")}</option>
                  </select>
                </div>
              </div>

              {/* Day routines list & management header */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-50 dark:bg-gray-900/60 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePrevManageDay}
                      className="px-2.5 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 cursor-pointer transition-colors shadow-2xs flex items-center gap-1 text-xs font-bold"
                      title={t("prevDay", "Previous Day")}
                    >
                      <ChevronRight className="w-4 h-4 rtl:rotate-0 ltr:rotate-180" />
                      <span>{t("prevDay", "Previous Day")}</span>
                    </button>

                    <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-1.5 px-2">
                      <CalendarCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      <span>{t("schedulesRoutinesForDay", "Registered activities for {{day}}", { day: getDayName(selectedDay) })}</span>
                    </h3>

                    <button
                      type="button"
                      onClick={handleNextManageDay}
                      className="px-2.5 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 cursor-pointer transition-colors shadow-2xs flex items-center gap-1 text-xs font-bold"
                      title={t("nextDay", "Next Day")}
                    >
                      <span>{t("nextDay", "Next Day")}</span>
                      <ChevronLeft className="w-4 h-4 rtl:rotate-0 ltr:rotate-180" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectTodayManage}
                      className="px-3 py-1.5 text-xs rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-extrabold hover:bg-indigo-100 dark:hover:bg-indigo-900 cursor-pointer shadow-2xs"
                    >
                      {t("schedulesBackToToday", "🕒 Back to Today")}
                    </button>

                    <button
                      onClick={handleOpenAddTask}
                      className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>{t("schedulesAddActivity", "+ Add Activity")}</span>
                    </button>
                  </div>
                </div>

                {/* Edit / Add Task Modal */}
                {isEditingTask && (
                  <div className="p-5 md:p-6 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/30 border-2 border-indigo-300 dark:border-indigo-800 space-y-4 animate-fadeIn shadow-md">
                    <div className="flex items-center justify-between border-b pb-3 border-indigo-200 dark:border-indigo-900">
                      <h4 className="text-sm md:text-base font-extrabold text-indigo-950 dark:text-indigo-200 flex items-center gap-2">
                        <Edit2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <span>{editingTaskId ? t("schedulesEditActivityTitle", "Edit Activity in Weekly Plan") : t("schedulesAddActivityTitle", "Define New Activity in Weekly Plan")}</span>
                      </h4>
                      <button 
                        onClick={() => setIsEditingTask(false)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-200/60 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Quick Presets Bar */}
                    <div className="space-y-1.5">
                      <span className="text-[11px] font-extrabold text-indigo-900 dark:text-indigo-200">
                        ⚡ {t("schedulesPresetTemplates", "Quick Preset Templates:")}
                      </span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handlePresetSelect('work')}
                          className="px-2.5 py-1 bg-white dark:bg-gray-900 hover:bg-indigo-100 text-indigo-900 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          💼 {t("schedulesPresetWork", "Work Shift (8 hours)")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePresetSelect('rehab')}
                          className="px-2.5 py-1 bg-white dark:bg-gray-900 hover:bg-emerald-100 text-emerald-900 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          🛡️ {t("schedulesPresetRehab", "Spine Rehab / Physio (30 mins)")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePresetSelect('education')}
                          className="px-2.5 py-1 bg-white dark:bg-gray-900 hover:bg-amber-100 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-800 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          🎓 {t("schedulesPresetEducation", "Study / Learning Session (2 hours)")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePresetSelect('cardio')}
                          className="px-2.5 py-1 bg-white dark:bg-gray-900 hover:bg-rose-100 text-rose-900 dark:text-rose-200 border border-rose-200 dark:border-rose-800 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          🔥 {t("schedulesPresetCardio", "Cardio & Walking (45 mins)")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePresetSelect('nutrition')}
                          className="px-2.5 py-1 bg-white dark:bg-gray-900 hover:bg-teal-100 text-teal-900 dark:text-teal-200 border border-teal-200 dark:border-teal-800 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          🍎 {t("schedulesPresetNutrition", "Nutrition & Meal Care (30 mins)")}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 text-xs pt-1">
                      
                      {/* Day of Week */}
                      <div className="md:col-span-4 space-y-1">
                        <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormDay", "Day of the week:")}</label>
                        <select
                          value={taskForm.dayOfWeek ?? selectedDay}
                          onChange={(e) => setTaskForm({ ...taskForm, dayOfWeek: Number(e.target.value) })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-bold"
                        >
                          {WEEKDAYS.map((w) => (
                            <option key={w.value} value={w.value}>{getDayName(w.value)}</option>
                          ))}
                        </select>
                      </div>

                      {/* Start Time */}
                      <div className="md:col-span-4 space-y-1">
                        <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormStartTime", "🕒 Start Time")}</label>
                        <input
                          type="time"
                          value={taskForm.time || '08:00'}
                          onChange={(e) => setTaskForm({ ...taskForm, time: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 font-mono text-center text-gray-900 dark:text-white font-bold"
                        />
                      </div>

                      {/* End Time */}
                      <div className="md:col-span-4 space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormEndTime", "🕒 End Time (Optional)")}</label>
                          {formDurationMinutes > 0 && (
                            <span className="text-[10px] font-mono font-bold text-indigo-600 dark:text-indigo-300">
                              ⏱️ {formatMinutesDuration(formDurationMinutes, lang)}
                            </span>
                          )}
                        </div>
                        <input
                          type="time"
                          value={taskForm.endTime || ''}
                          onChange={(e) => setTaskForm({ ...taskForm, endTime: e.target.value })}
                          placeholder="09:00"
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 font-mono text-center text-gray-900 dark:text-white font-bold"
                        />
                      </div>

                      {/* Category */}
                      <div className="md:col-span-4 space-y-1">
                        <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormCategory", "Category:")}</label>
                        <select
                          value={taskForm.category || 'work'}
                          onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-bold"
                        >
                          <option value="work">{t("schedulesCatWork", "Work & Employment")}</option>
                          <option value="rehab">{t("schedulesCatRehab", "Physiotherapy & Back Rehab")}</option>
                          <option value="fat-loss">{t("schedulesCatFatLoss", "Workout & Cardio")}</option>
                          <option value="swedish">{t("schedulesCatSwedish", "Education & Learning (Swedish/SFI)")}</option>
                          <option value="nutrition">{t("schedulesCatNutrition", "Nutrition & Health")}</option>
                          <option value="routine">{t("schedulesCatRoutine", "Personal Routine")}</option>
                        </select>
                      </div>

                      {/* Title */}
                      <div className="md:col-span-8 space-y-1">
                        <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormTitle", "📝 Routine Activity Title *")}</label>
                        <div className="flex gap-1.5 items-center">
                          <input
                            type="text"
                            required
                            placeholder={t("schedulesFormTitlePlaceholder", "e.g. Work shift, physiotherapy, Swedish class, music practice...")}
                            value={taskForm.title || ''}
                            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                            className="flex-1 px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-bold"
                          />
                          <VoiceInputButton
                            currentValue={taskForm.title || ''}
                            onUpdate={(val) => setTaskForm({ ...taskForm, title: val })}
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div className="md:col-span-12 space-y-1">
                        <label className="font-extrabold text-gray-800 dark:text-gray-200">{t("schedulesFormDesc", "💬 Details or Goals (Optional)")}</label>
                        <textarea
                          rows={2}
                          placeholder={t("schedulesFormDescPlaceholder", "Optional description, goals, or execution details for this activity...")}
                          value={taskForm.description || ''}
                          onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-medium"
                        />
                      </div>

                      {/* Task Requirement Status */}
                      <div className="md:col-span-12 p-3 bg-white dark:bg-gray-900 rounded-xl border border-indigo-200 dark:border-indigo-900 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <label className="flex items-center gap-2.5 cursor-pointer text-xs font-extrabold text-gray-800 dark:text-gray-200">
                          <input
                            type="checkbox"
                            checked={taskForm.isRequired !== false}
                            onChange={(e) => setTaskForm({ ...taskForm, isRequired: e.target.checked })}
                            className="accent-indigo-600 w-4 h-4 rounded cursor-pointer"
                          />
                          <span>{t("isRequiredTaskLabel", "Required activity (in daily mandatory task list)")}</span>
                        </label>
                        <span className={`text-[11px] font-extrabold px-3 py-1 rounded-lg ${
                          taskForm.isRequired !== false 
                            ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800' 
                            : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                        }`}>
                          {taskForm.isRequired !== false ? t("taskReqStandard", "Required / Standard") : t("taskReqBonus", "Optional / Bonus")}
                        </span>
                      </div>

                      {/* Special Flags & Notifications */}
                      <div className="md:col-span-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-indigo-200 dark:border-indigo-900">
                        
                        <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={taskForm.wentToWorkChecked || false}
                            onChange={(e) => setTaskForm({ ...taskForm, wentToWorkChecked: e.target.checked })}
                            className="accent-indigo-600 w-4 h-4 rounded"
                          />
                          <span className="text-xs font-extrabold text-gray-800 dark:text-gray-200">
                            {t("schedulesFormIsWork", "Register as work shift")}
                          </span>
                        </label>

                        {/* General Educational Activity Label (Replaces specific SFI restriction) */}
                        <label className="flex items-center gap-2 p-2.5 rounded-xl border border-amber-300 dark:border-amber-800/80 bg-amber-50/50 dark:bg-amber-950/30 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={taskForm.attendedSfiChecked || false}
                            onChange={(e) => setTaskForm({ ...taskForm, attendedSfiChecked: e.target.checked })}
                            className="accent-amber-500 w-4 h-4 rounded"
                          />
                          <span className="text-xs font-black text-amber-900 dark:text-amber-200">
                            {t("schedulesFormIsEducation", "This is an educational activity (course/study/music)")}
                          </span>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={taskForm.phoneNotificationEnabled ?? true}
                            onChange={(e) => setTaskForm({ ...taskForm, phoneNotificationEnabled: e.target.checked })}
                            className="accent-indigo-600 w-4 h-4 rounded"
                          />
                          <span className="text-xs font-extrabold text-gray-800 dark:text-gray-200 flex items-center gap-1">
                            <Bell className="w-3.5 h-3.5 text-indigo-500" />
                            {t("schedulesFormPhoneAlarm", "Alarm & Notification")}
                          </span>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={taskForm.reminderEnabled || false}
                            onChange={(e) => setTaskForm({ ...taskForm, reminderEnabled: e.target.checked })}
                            className="accent-emerald-500 w-4 h-4 rounded"
                          />
                          <span className="text-xs font-extrabold text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {t("schedulesFormGoogleCal", "Google Calendar (.ics)")}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t pt-3 border-indigo-200 dark:border-indigo-900">
                      <button
                        onClick={() => setIsEditingTask(false)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl text-xs font-bold cursor-pointer"
                      >
                        {t("schedulesCancel", "Cancel")}
                      </button>
                      <button
                        onClick={handleSaveTask}
                        className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 cursor-pointer shadow-sm"
                      >
                        <Save className="w-4 h-4" />
                        {t("schedulesSaveRoutine", "Save Routine")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Routine task List UI */}
                {selectedDayTasks.length === 0 ? (
                  <div className="p-8 text-center bg-gray-50 dark:bg-gray-900/40 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl space-y-3 animate-fadeIn">
                    <AlertCircle className="w-8 h-8 text-indigo-400 mx-auto" />
                    <p className="text-sm text-gray-800 dark:text-gray-200 font-extrabold">
                      {searchQuery || categoryFilter !== 'all' 
                        ? t("schedulesNoTasksFilter", "No tasks found matching your filter.") 
                        : t("schedulesNoRoutinesForDay", "No routines scheduled for {{day}}.", { day: getDayName(selectedDay) })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                      {t("schedulesNoRoutinesHint", "Click ")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDayTasks.map((task) => {
                      const isPhoneBellActive = task.phoneNotificationEnabled ?? true;
                      const isCalendarActive = task.reminderEnabled ?? false;
                      const loc = getLocalizedTaskContent(task);
                      const dur = getTaskDurationMinutes(task.time, task.endTime);
                      const catCfg = getCategoryConfig(task.category);
                      const gCalUrl = createGoogleCalendarUrl(task);

                      return (
                        <div 
                          key={task.id}
                          className={`p-4 rounded-2xl border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-900 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all shadow-2xs ${catCfg.accentBar}`}
                        >
                          <div className="space-y-2.5 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Category Badge */}
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-extrabold border ${catCfg.colorBg} ${catCfg.colorText} ${catCfg.colorBorder}`}>
                                {catCfg.icon}
                                <span>{catCfg.label}</span>
                              </span>

                              {/* Time Range */}
                              <span className="px-2.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg font-mono text-xs font-bold border border-gray-200 dark:border-gray-700" dir="ltr">
                                🕒 {task.time}{task.endTime ? ` - ${task.endTime}` : ''}
                              </span>

                              {dur > 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono font-bold">
                                  ({formatMinutesDuration(dur, lang)})
                                </span>
                              )}

                              {task.isRequired !== false ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 font-bold border border-indigo-200 dark:border-indigo-800">
                                  {t("dashTaskRequired", "Required")}
                                </span>
                              ) : (
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 font-bold border border-amber-200 dark:border-amber-800">
                                  {t("dashTaskOptional", "Optional")}
                                </span>
                              )}

                              {task.wentToWorkChecked && (
                                <span className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 rounded-lg font-bold">
                                  {t("schedulesWorkTag", "Work Shift")}
                                </span>
                              )}
                              {task.attendedSfiChecked && (
                                <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300 rounded-lg font-bold">
                                  {t("schedulesStudyTag", "Study / SFI")}
                                </span>
                              )}

                              {/* 1-Click Interactive Alarm Toggle Chip */}
                              <button
                                type="button"
                                onClick={() => handleToggleTaskNotification(task.id)}
                                className={`text-[11px] px-2.5 py-1 rounded-lg font-extrabold flex items-center gap-1.5 transition-all cursor-pointer border ${
                                  isPhoneBellActive 
                                    ? 'bg-indigo-100 hover:bg-indigo-200 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800' 
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500 border-gray-200 dark:border-gray-700 line-through'
                                }`}
                                title={t("schedulesPhoneAlarmTooltip", "Alarm and phone notification are active")}
                              >
                                {isPhoneBellActive ? <Bell className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> : <BellOff className="w-3.5 h-3.5" />}
                                <span>{isPhoneBellActive ? t("schedulesPhoneAlarmActive", "Phone Alarm: Active") : t("schedulesPhoneAlarmInactive", "Phone Alarm: Disabled")}</span>
                              </button>

                              {/* 1-Click Interactive Google Calendar Toggle Chip */}
                              <button
                                type="button"
                                onClick={() => handleToggleTaskReminder(task.id)}
                                className={`text-[11px] px-2.5 py-1 rounded-lg font-extrabold flex items-center gap-1.5 transition-all cursor-pointer border ${
                                  isCalendarActive 
                                    ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800' 
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500 border-gray-200 dark:border-gray-700 line-through'
                                }`}
                                title={t("schedulesGoogleCalTooltip", "Add to Google Calendar to receive personal reminder")}
                              >
                                <Calendar className={`w-3.5 h-3.5 ${isCalendarActive ? 'text-emerald-600 dark:text-emerald-400' : ''}`} />
                                <span>{isCalendarActive ? t("schedulesGoogleCalActive", "Google Calendar: Active") : t("schedulesGoogleCalInactive", "Google Calendar: Disabled")}</span>
                              </button>

                              {/* Google Calendar Link Direct Launcher */}
                              <a
                                href={gCalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 rounded-md font-bold flex items-center gap-1 border border-blue-200 dark:border-blue-800"
                                title={t("schedulesGoogleCalDirectTitle", "Add directly to Google Calendar (Web)")}
                              >
                                <span>{t("schedulesAddToGoogleCalDirect", "Add to Google Cal")}</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>

                            <div>
                              <h4 className="font-extrabold text-sm md:text-base text-gray-900 dark:text-white">{loc.title}</h4>
                              {loc.description && (
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed font-medium">{loc.description}</p>
                              )}
                            </div>

                            {/* Quick Copy task to specific weekday */}
                            <div className="flex items-center gap-1.5 pt-1.5 flex-wrap">
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-bold">
                                {t("schedulesQuickCopyTo", "Quick copy to:")}
                              </span>
                              <div className="flex gap-1.5 flex-wrap">
                                {WEEKDAYS.filter(w => w.value !== Number(task.dayOfWeek)).map((w) => {
                                  return (
                                    <button
                                      key={w.value}
                                      type="button"
                                      onClick={() => handleDuplicateTaskToDay(task, w.value)}
                                      className="px-2 py-0.5 text-[10px] bg-gray-100 hover:bg-indigo-100 dark:bg-gray-800 dark:hover:bg-indigo-950 text-gray-700 hover:text-indigo-700 dark:text-gray-300 dark:hover:text-indigo-300 rounded-lg font-extrabold transition-colors cursor-pointer border border-gray-200 dark:border-gray-700"
                                      title={t("schedulesCopiedSuccess", "Activity '{{title}}' copied to {{day}} successfully.", { title: loc.title, day: getDayName(w.value) })}
                                    >
                                      +{getDayName(w.value)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Action Controls */}
                          <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                            <button
                              onClick={() => handleOpenEditTask(task)}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-indigo-100 dark:bg-gray-800 dark:hover:bg-indigo-950/60 text-gray-700 hover:text-indigo-700 dark:text-gray-200 rounded-xl text-xs font-extrabold transition-colors cursor-pointer flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 shadow-2xs"
                              title={t("schedulesEditFull", "Edit")}
                            >
                              <Edit2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                              <span>{t("schedulesEditFull", "Edit")}</span>
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="p-2 bg-gray-100 hover:bg-rose-100 dark:bg-gray-800 dark:hover:bg-rose-950/60 text-gray-500 hover:text-rose-600 rounded-xl transition-colors cursor-pointer border border-gray-200 dark:border-gray-700 shadow-2xs"
                              title={t("schedulesDeleteActivity", "Delete activity")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Right Column: Weekly Attendance & Cloud D1 Sync Info */}
        <div className="lg:col-span-4 space-y-4">
          
          {/* Shift attendance tracker summary card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-200 dark:border-gray-700 shadow-xs space-y-4">
            <h3 className="text-xs font-extrabold text-gray-600 dark:text-gray-300 uppercase tracking-wider block">
              {t("schedulesWeeklyAttendanceStatus", "Weekly Attendance Status (Mon - Sun)")}
            </h3>

            {/* Attendance chart layout */}
            <div className="space-y-2">
              {weeklyLogs.map((log) => (
                <div key={log.date} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-900/60 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700/60 transition-all hover:border-gray-300">
                  <span className="font-extrabold text-gray-900 dark:text-white">{log.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`px-2 py-0.5 rounded-md font-extrabold text-[10px] ${
                      log.wentToWork 
                        ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800' 
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800/60 dark:text-gray-500'
                    }`}>
                      {log.wentToWork ? t("schedulesWorkPresence", "Work Shift") : t("schedulesNonWorkDay", "Off")}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md font-extrabold text-[10px] ${
                      log.attendedSfi 
                        ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-800' 
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800/60 dark:text-gray-500'
                    }`}>
                      {log.attendedSfi ? t("schedulesClassPresence", "Study Class") : t("schedulesNoStudyDay", "No Class")}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Total indicators */}
            <div className="grid grid-cols-2 gap-3 text-center border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase block font-extrabold">
                  {t("schedulesTotalWorkShifts", "Total Work Shifts")}
                </span>
                <span className="font-extrabold text-gray-900 dark:text-white font-mono text-xl">
                  {formatNumberByLang(totalWorkDays, lang)}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 block font-medium">
                  {t("schedulesWorkDaysThisWeek", "work days this week")}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase block font-extrabold">
                  {t("schedulesTotalClasses", "Total Study Classes")}
                </span>
                <span className="font-extrabold text-gray-900 dark:text-white font-mono text-xl">
                  {formatNumberByLang(totalSfiDays, lang)}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 block font-medium">
                  {t("schedulesClassesThisWeek", "sessions this week")}
                </span>
              </div>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}
